import { useCallback, useEffect, useState } from 'react';
import { businessApiRoute, get } from '../../../lib/Api';
import { useCompleteStep } from '../../../lib/Onboarding';
import {
    type PaymentIntegrationProps,
    PaymentProcessor,
    type StripeEndpointResponse,
} from './Payment.types';
import TStrings from './Payment.i18n';
import PaymentProcessorCard from './PaymentProcessorCard';
import Modal from '~/components/Modal';
import useDeleteApi from '~/lib/useDeleteApi';
import useDelete from '~/lib/useDelete';
import * as Toast from '~/lib/Toast';
import StripeIcon from '~/components/icons/StripeIcon';

type StripeIntegrationProps = PaymentIntegrationProps;

const StripeIntegration = ({
    className,
    onIntegrationEnabled,
    disabled,
}: StripeIntegrationProps) => {
    const [stripeData, setStripeData] = useState<StripeEndpointResponse | null>(
        null,
    );
    const [isModalShowing, setIsModalShowing] = useState(false);
    const { complete } = useCompleteStep();

    const { saveDelete, isDeleting } = useDelete(
        `/integrations/payments/stripe/disconnect`,
        {
            prompt: 'Are you sure you want to disconnect Stripe?',
        },
    );

    useEffect(() => {
        get(businessApiRoute(`/integrations/payments/stripe`)).then(result => {
            console.log('got result', result);
            setStripeData(result);

            if (result?.isConfigured) {
                onIntegrationEnabled(PaymentProcessor.Stripe);
                complete('integration-payments');
            }
        });
    }, []);

    const connectCallback = useCallback(() => {
        if (stripeData?.url) {
            window.location.href = stripeData.oauth;
        }
    }, [stripeData?.url]);

    function onButtonClick() {
        if (stripeData?.isConfigured) {
            setIsModalShowing(true);
        } else {
            connectCallback();
        }
    }

    function doDisconnect() {
        saveDelete().then(result => {
            setStripeData(result);
            setIsModalShowing(false);
            Toast.success('Disconnected from Stripe');
        });
    }

    return (
        <>
            <PaymentProcessorCard
                enabled={!disabled || stripeData?.isConfigured}
                buttonText={
                    stripeData?.isConfigured
                        ? TStrings.MANAGE_STRIPE_BUTTON
                        : TStrings.CONNECT_STRIPE_BUTTON
                }
                className={className}
                icon={
                    <StripeIcon className="w-[100px] -ml-3" />
                }
                description={TStrings.STRIPE_DESCRIPTION}
                onConnectClick={onButtonClick}
                hasInit={!!stripeData}
                isConnected={stripeData?.isConfigured}
                isLoading={!stripeData}
                subtitle1={
                    stripeData?.isConfigured && !stripeData?.isChargesEnabled
                        ? TStrings.UNABLE_TO_RECEIVE_PAYMENTS
                        : ''
                }
                subtitle2={
                    stripeData?.isConfigured && !!stripeData?.areRequirementsDue
                        ? TStrings.STRIPE_STEPS_REQUIRED
                        : ''
                }
            />
            <Modal
                isOpen={isModalShowing}
                onClose={() => setIsModalShowing(false)}
            >
                <h2 className="mb-6">Stripe</h2>
                <button
                    type="button"
                    className="btn-light-pill"
                    onClick={connectCallback}
                >
                    Reconnect
                </button>

                <button
                    type="button"
                    className="btn-pink-pill ml-4"
                    onClick={doDisconnect}
                >
                    {isDeleting ? 'Disconnecting...' : 'Disconnect'}
                </button>
            </Modal>
        </>
    );
};

export default StripeIntegration;
